import React, { useRef, useState, useEffect } from 'react';
import { 
  IconButton, 
  Tooltip, 
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import PushPinIcon from '@mui/icons-material/PushPin';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const ChartActions = ({ chartId, title, chartRef, chartType }) => {
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [chartImage, setChartImage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Create a chart image when opening fullscreen view
  const getCanvas = () => {
    const inst = chartRef?.current;
    if (!inst) return null;
    return inst.canvas ?? inst.chartInstance?.canvas ?? null;
  };

  useEffect(() => {
    if (!fullscreenOpen) return;
    const canvas = getCanvas();
    if (!canvas) return;
    try {
      const imageUrl = canvas.toDataURL('image/png');
      setChartImage(imageUrl);
    } catch (err) {
      console.error('Error creating chart image:', err);
      setChartImage(null);
    }
  }, [fullscreenOpen, chartRef]);
  
  const handleFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  const getChartImage = () => {
    const canvas = getCanvas();
    if (!canvas) return null;
    try {
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error getting chart image:', error);
      return null;
    }
  };

  const handleDownload = () => {
    const imageUrl = getChartImage();
    if (!imageUrl) {
      setSnackbar({ 
        open: true, 
        message: 'Could not download chart. Please try again.', 
        severity: 'error' 
      });
      return;
    }
    
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.download = `${title.replace(/\s+/g, '-')}.png`;
    link.href = imageUrl;
    link.click();
    
    setSnackbar({ 
      open: true, 
      message: 'Chart downloaded successfully!', 
      severity: 'success' 
    });
  };

  const handlePinToDashboard = async () => {
    try {
      const imageData = getChartImage();
      if (!imageData) {
        throw new Error('Chart image not available');
      }
      
      // Get chart data if available
      let chartData = null;
      let dataEndpoint = null;
      
      const inst = chartRef?.current?.chartInstance ?? chartRef?.current;
      if (inst && typeof inst.data !== 'undefined') {
        try {
          chartData = JSON.parse(JSON.stringify(inst.data));
        } catch (e) {
          console.warn('Could not serialize chart data for pin:', e);
        }

        // Determine endpoint based on chart ID
        if (chartId.includes('ev')) {
          dataEndpoint = '/api/vehicles/ev-analytics';
        } else if (chartId.includes('ice')) {
          dataEndpoint = '/api/vehicles/ice-analytics';
        } else if (chartId.includes('comparison')) {
          dataEndpoint = '/api/vehicles/comparison';
        } else if (chartId.includes('sales')) {
          dataEndpoint = '/api/vehicles/sales';
        } else {
          dataEndpoint = '/api/vehicles/ev-analytics';
        }
      }
      
      // Send to backend to store in user's dashboard
      await axios.post('/api/dashboard/pin', {
        chartId,
        title,
        chartType,
        imageData,
        chartData,
        dataEndpoint
      });
      
      setSnackbar({ 
        open: true, 
        message: 'Chart pinned to dashboard!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error pinning chart:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to pin chart to dashboard', 
        severity: 'error' 
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Tooltip title="View fullscreen">
          <IconButton 
            size="small" 
            onClick={handleFullscreen}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Download as image">
          <IconButton 
            size="small" 
            onClick={handleDownload}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Pin to dashboard">
          <IconButton 
            size="small" 
            onClick={handlePinToDashboard}
          >
            <PushPinIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title}
          <IconButton onClick={handleCloseFullscreen}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {chartImage ? (
            <img 
              src={chartImage} 
              alt={title}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }} 
            />
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              Cannot display chart. Please try again.
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download
          </Button>
          <Button onClick={handlePinToDashboard} startIcon={<PushPinIcon />}>
            Pin to Dashboard
          </Button>
          <Button onClick={handleCloseFullscreen}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChartActions; 